import React, { useState, useEffect } from "react";
import { MRadio } from "@/components/ui/atoms/MRadio";
import { IconPlus, IconCircleX, IconX } from "@tabler/icons-react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MHeader from "@/components/ui/organisms/MHeader";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { useTypeAhead } from "@/hooks/useTypeAhead";
// Removed unused import
import { MDialog } from "@/components/ui/atoms/MDialog";
import "../profile.css";
import { getMemberInfo } from "@/hooks/useMembers";
import { useProfileField } from "@/hooks/useHealthProfile";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MLoader from "../../../components/ui/atoms/MLoader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNavigationSource } from "@/hooks/useNavigationSource";

import {
  MedicalDetailType,
  type IBirthComplication,
  BirthComplications as BirthComplicationsEnum,
  BirthComplicationsStatus,
} from "@/types/health-profile";
import MSpacer from "@/components/ui/atoms/MSpacer";
import styles from "./birth_complications.module.css";
import MInputField from "@/components/ui/atoms/MInputField";
import MInfoCard from "@/components/ui/atoms/MInfoCard";
import { MButton } from "@/components/ui/atoms/MButtons";

const BirthComplications: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const { navigateBackIfExists } = useNavigationSource();
  const {
    fieldValue: birthComplications,
    saveFieldNoRefetch: saveBirthComplicationsNoRefetch,
    loading,
  } = useProfileField<Record<BirthComplicationsEnum, IBirthComplication>>(
    memberId,
    MedicalDetailType.BIRTH_COMPLICATIONS,
    {
      [BirthComplicationsEnum.PREMATURE_BIRTH]: { status: null },
      [BirthComplicationsEnum.LOW_BIRTH_WEIGHT]: { status: null },
      [BirthComplicationsEnum.COMPLICATIONS]: { status: null },
    }
  );

  // Local UI state - using the enum values directly
  const [answers, setAnswers] = useState<{
    [BirthComplicationsEnum.PREMATURE_BIRTH]: BirthComplicationsStatus | null;
    [BirthComplicationsEnum.LOW_BIRTH_WEIGHT]: BirthComplicationsStatus | null;
    [BirthComplicationsEnum.COMPLICATIONS]: BirthComplicationsStatus | null;
  }>({
    [BirthComplicationsEnum.PREMATURE_BIRTH]: null, // Not Sure by default
    [BirthComplicationsEnum.LOW_BIRTH_WEIGHT]: null, // Not Sure by default
    [BirthComplicationsEnum.COMPLICATIONS]: null, // Not Sure by default
  });
  const [showDropdownDrawer, setShowDropdownDrawer] = useState(false);
  const {
    values: complicationOptions,
    loading: typeaheadLoading,
    fetch: fetchComplications,
  } = useTypeAhead("birth_complications_child");
  const [stagedComplications, setStagedComplications] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [complicationToDelete, setComplicationToDelete] = useState<
    string | null
  >(null);

  // Sync local state with backend value
  useEffect(() => {
    // Always sync all toggles and staged complications from backend
    if (birthComplications) {
      setAnswers({
        [BirthComplicationsEnum.PREMATURE_BIRTH]:
          birthComplications[BirthComplicationsEnum.PREMATURE_BIRTH]?.status ||
          null,
        [BirthComplicationsEnum.LOW_BIRTH_WEIGHT]:
          birthComplications[BirthComplicationsEnum.LOW_BIRTH_WEIGHT]?.status ||
          null,
        [BirthComplicationsEnum.COMPLICATIONS]:
          birthComplications[BirthComplicationsEnum.COMPLICATIONS]?.status ||
          null,
      });

      // Set staged complications from the content array
      setStagedComplications(
        birthComplications[BirthComplicationsEnum.COMPLICATIONS]?.content || []
      );
    }
  }, [birthComplications]);

  // For new/first time flow
  const isUnchanged =
    birthComplications &&
    // Check if premature birth status is unchanged
    answers[BirthComplicationsEnum.PREMATURE_BIRTH] ===
      birthComplications[BirthComplicationsEnum.PREMATURE_BIRTH]?.status &&
    // Check if low birth weight status is unchanged
    answers[BirthComplicationsEnum.LOW_BIRTH_WEIGHT] ===
      birthComplications[BirthComplicationsEnum.LOW_BIRTH_WEIGHT]?.status &&
    // Check if complications status is unchanged
    answers[BirthComplicationsEnum.COMPLICATIONS] ===
      birthComplications[BirthComplicationsEnum.COMPLICATIONS]?.status &&
    // Check if staged complications are unchanged
    JSON.stringify(stagedComplications) ===
      JSON.stringify(
        birthComplications[BirthComplicationsEnum.COMPLICATIONS]?.content || []
      );

  const isComplete =
    // All questions must be answered
    answers[BirthComplicationsEnum.PREMATURE_BIRTH] !== null &&
    answers[BirthComplicationsEnum.LOW_BIRTH_WEIGHT] !== null &&
    answers[BirthComplicationsEnum.COMPLICATIONS] !== null &&
    // If complications is YES, must have at least one complication
    (answers[BirthComplicationsEnum.COMPLICATIONS] !==
      BirthComplicationsStatus.YES ||
      stagedComplications.length > 0);

  // Handle Yes/No/Not Sure selection
  const handleSelect = (
    key: BirthComplicationsEnum,
    value: BirthComplicationsStatus | null
  ) => {
    // Update the local state
    setAnswers((prev) => ({
      ...prev,
      [key]: value,
    }));

    // Handle complications specific logic
    if (key === BirthComplicationsEnum.COMPLICATIONS) {
      if (value !== BirthComplicationsStatus.YES) {
        // Clear complications if not YES
        setStagedComplications([]);
      } else {
        // Show dropdown to add complications if YES
        fetchComplications();
        setShowDropdownDrawer(true);
      }
    }
  };

  // Save to backend
  const handleSave = async () => {
    if (!isComplete) return;
    try {
      const updatedBirthComplications = {
        [BirthComplicationsEnum.PREMATURE_BIRTH]: {
          status: answers[BirthComplicationsEnum.PREMATURE_BIRTH],
        },
        [BirthComplicationsEnum.LOW_BIRTH_WEIGHT]: {
          status: answers[BirthComplicationsEnum.LOW_BIRTH_WEIGHT],
        },
        [BirthComplicationsEnum.COMPLICATIONS]: {
          status: answers[BirthComplicationsEnum.COMPLICATIONS],
          content:
            answers[BirthComplicationsEnum.COMPLICATIONS] ===
            BirthComplicationsStatus.YES
              ? stagedComplications
              : [],
        },
      };

      await saveBirthComplicationsNoRefetch(updatedBirthComplications);

      // Navigate back to source if exists, otherwise go to health profile
      const didNavigate = navigateBackIfExists();
      if (!didNavigate) {
        navigate("/profile/health-profile");
      }
    } catch (error) {
      toast.showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    }
  };

  const handleDelete = () => {
    if (complicationToDelete) {
      // Update the staged complications
      const updatedComplications = stagedComplications.filter(
        (c) => c !== complicationToDelete
      );
      setStagedComplications(updatedComplications);

      // If no complications left, update the complications status to NO
      if (updatedComplications.length === 0) {
        setAnswers((prev) => ({
          ...prev,
          [BirthComplicationsEnum.COMPLICATIONS]: BirthComplicationsStatus.NO,
        }));
      }

      setShowDeleteModal(false);
      setComplicationToDelete(null);
    }
  };

  // State for custom complication drawer
  const [showAddCustomComplication, setShowAddCustomComplication] =
    useState(false);
  const [customComplication, setCustomComplication] = useState("");

  const getComplicationOptions = () => {
    const opts = complicationOptions.map((c: string) => ({
      value: c,
      label: c,
    }));
    opts.push({ value: "Other", label: t("common.other") });
    return opts;
  };

  const handleAddComplication = (selected: string[]) => {
    setStagedComplications(selected);

    // Update the answer state
    setAnswers((prev) => ({
      ...prev,
      [BirthComplicationsEnum.COMPLICATIONS]: BirthComplicationsStatus.YES,
    }));

    setShowDropdownDrawer(false);
  };

  const handleComplicationsSelectionDone = (selected: string[]) => {
    if (selected.includes("Other")) {
      setShowAddCustomComplication(true);
      setShowDropdownDrawer(false);
      return;
    }

    handleAddComplication(selected);
  };

  const handleAddCustomComplicationDone = () => {
    const trimmed = customComplication.trim();
    const newComplications = stagedComplications.filter((c) => c !== "Other");
    if (trimmed && !stagedComplications.includes(trimmed)) {
      newComplications.push(trimmed);
      setStagedComplications(newComplications);
    }

    // Update the answer state
    setAnswers((prev) => ({
      ...prev,
      [BirthComplicationsEnum.COMPLICATIONS]: BirthComplicationsStatus.YES,
    }));

    setShowAddCustomComplication(false);
    setShowDropdownDrawer(false);
    setCustomComplication("");
  };

  // UI rendering
  if (loading) return <MLoader />;

  return (
    <div className="profile-page-root">
      <MHeader module="birthComplications" memberInfo={memberInfo} />
      <MSpacer mt={32}/>
      {/* Always show all three questions */}
      <div className="profile-input-container pb-28">
        <div className={styles.questions_container}>
          {Object.values(BirthComplicationsEnum).map((complicationType) => (
            <div key={complicationType} className={styles.question_item}>
              <div className="profile-input-label">
                {t(
                  `healthProfile.birthComplications.questions.${complicationType}`
                )}
              </div>
              <div className={styles.options_container}>
                {Object.values(BirthComplicationsStatus).map((status) => (
                  <div key={status} className={styles.radio_container}>
                    <MRadio
                      name={complicationType}
                      checked={answers[complicationType] === status}
                      onChange={() => handleSelect(complicationType, status)}
                    />
                    <span className="profile-choice-btn-label">
                      {t(`healthProfile.birthComplications.options.${status}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {/* Show staged complications only if Yes is selected */}
        {answers[BirthComplicationsEnum.COMPLICATIONS] ===
          BirthComplicationsStatus.YES &&
          stagedComplications.length > 0 && (
            <div className={styles.complications_container}>
              <div className={styles.complications_header}>
                <div className={styles.complications_header_label}>
                  {t("healthProfile.birthComplications.otherComplications")}
                </div>
                <div
                  className={styles.edit_button}
                  onClick={() => {
                    setShowDropdownDrawer(true);
                  }}
                >
                  {t("common.edit")}
                </div>
              </div>
              <div className={styles.complications_list}>
                {stagedComplications.map((option) => (
                  <div className="w-full">
                    <MInfoCard
                      primaryLabel={option}
                      variant="custom"
                      rightActionComponent={
                        <div
                          onClick={() => {
                            setComplicationToDelete(option);
                            setShowDeleteModal(true);
                          }}
                        >
                          <IconX className={styles.delete_button_icon} />
                        </div>
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        <div className={`confirm-footer ${styles.confirm_footer_override}`}>
          <MButton
            type="button"
            variant="filled"
            size="lg"
            className="w-full"
            disabled={isUnchanged || !isComplete}
            onClick={handleSave}
            text={t("common.done")}
          />
        </div>
      </div>

      {/* Show complications section if YES is selected */}
      {showDropdownDrawer && (
        <div className="mt-[40px]">
          <div className="mb-3 flex justify-between items-center">
            <div className="cta-label">
              {t("healthProfile.birthComplications.otherComplications")}
            </div>
            <button
              className="text-primary-indigo-500 flex items-center gap-1"
              onClick={() => {
                fetchComplications();
                setShowDropdownDrawer(true);
              }}
            >
              <IconPlus size={16} />
              <span>{t("common.add")}</span>
            </button>
          </div>

          {/* List of complications */}
          <div className="flex flex-col gap-4">
            {stagedComplications.map((complication) => (
              <div
                key={complication}
                className="flex items-center justify-between p-3 border-b border-gray-200"
              >
                <span>{complication}</span>
                <button
                  className="text-gray-500 hover:text-red-500"
                  onClick={() => {
                    setComplicationToDelete(complication);
                    setShowDeleteModal(true);
                  }}
                  aria-label="Delete complication"
                >
                  <IconCircleX size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <MDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setComplicationToDelete(null);
        }}
        title={t("healthProfile.birthComplications.delete")}
        description={t("healthProfile.birthComplications.deleteDescription", {
          complication: complicationToDelete,
        })}
        primaryCtaText={t("common.yes")}
        onPrimaryCta={handleDelete}
        secondaryCtaText={t("common.no")}
        onSecondaryCta={() => {
          setShowDeleteModal(false);
          setComplicationToDelete(null);
        }}
      />

      {/* Always render the complication dropdown drawer */}
      <MDropDownDrawer
        open={showDropdownDrawer}
        onClose={() => setShowDropdownDrawer(false)}
        title={t("healthProfile.birthComplications.selectComplications")}
        items={getComplicationOptions()}
        variant="multi"
        selectedValues={stagedComplications}
        onSelect={(value) =>
          Array.isArray(value)
            ? setStagedComplications(value)
            : setStagedComplications([value])
        }
        onDone={handleComplicationsSelectionDone}
        doneLoading={typeaheadLoading}
      />

      {/* Add Custom Complication Drawer */}
      <FooterDrawer
        open={showAddCustomComplication}
        onClose={() => {
          setShowAddCustomComplication(false);
          setShowDropdownDrawer(true);
          setCustomComplication("");
        }}
        title={t("healthProfile.birthComplications.addComplication")}
        showClose
        height="calc(100vh - 56px)"
        onDone={handleAddCustomComplicationDone}
        doneText={t("common.done")}
        doneDisabled={
          !customComplication.trim() ||
          stagedComplications.includes(customComplication.trim())
        }
        donePosition="top"
      >
        <div className={styles.add_complication_container}>
          <MInputField
            label={t("healthProfile.birthComplications.complicationName")}
            value={customComplication}
            onChange={(e) => setCustomComplication(e.target.value)}
            placeholder={t("common.notAnswered")}
            autoFocus
          />
        </div>
      </FooterDrawer>
    </div>
  );
};

export default BirthComplications;
